import { splitPlacementPoints } from "../ties.js";
import type { Format, RoundResult } from "./types.js";
import {
  buildScoreMap,
  computeHandicaps,
  holeCount,
  scoringHoles,
  netOnHole,
  parByHole,
} from "./util.js";

/** Standard net Stableford scale: 2 points for net par, -1/+1 per stroke away, floored at 0. */
function stablefordPoints(net: number, par: number): number {
  return Math.max(0, 2 - (net - par));
}

export const stableford: Format = {
  id: "stableford",
  name: "Stableford",
  scope: "individual",
  uses: { pointTable: true, handicapAllowance: true },
  entry: "perPlayer",
  defaults: { handicapAllowance: 100 },
  configSchema: { type: "object", properties: {} },
  perHoleSchema: null,

  score({ round, course, players, scores, onlyHoles }): RoundResult {
    const holes = scoringHoles(round, course, onlyHoles);
    const par = parByHole(course, round);
    const scoreMap = buildScoreMap(scores);
    const handicaps = computeHandicaps(round, course, players);

    const entries: { id: string; value: number }[] = [];
    for (const p of players) {
      let complete = true;
      let total = 0;
      for (const h of holes) {
        const net = netOnHole(scoreMap, handicaps, p.id, h);
        if (net == null) {
          complete = false;
          break;
        }
        total += stablefordPoints(net, par.get(h) ?? 4);
      }
      if (complete) entries.push({ id: p.id, value: total });
    }

    // Higher Stableford total is better.
    const points = splitPlacementPoints(entries, round.pointTable, { lowerIsBetter: false });
    const stablefordTotals: Record<string, number> = {};
    for (const e of entries) stablefordTotals[e.id] = e.value;

    const pointsRecord: Record<string, number> = {};
    for (const p of players) pointsRecord[p.id] = points.get(p.id) ?? 0;

    // pointsTotal, not netTotal: this is a Stableford points total, where higher is
    // better. Reported as a net stroke total it was read as strokes by the
    // lowestNetTotal tiebreak, which ranks the best round last.
    return {
      roundId: round.id,
      points: pointsRecord,
      pointsTotal: stablefordTotals,
      provisional: Boolean(onlyHoles),
    };
  },

  /** What each hole was worth. The round total is a sum of eighteen small decisions,
   * and "why 31?" is only answerable hole by hole. */
  explainHoles({ round, course, players, scores, playerId }) {
    const holes = scoringHoles(round, course);
    const par = parByHole(course, round);
    const scoreMap = buildScoreMap(scores);
    const handicaps = computeHandicaps(round, course, players);
    const notes = new Map<number, string>();

    for (const h of holes) {
      const net = netOnHole(scoreMap, handicaps, playerId, h);
      if (net == null) continue;
      const pts = stablefordPoints(net, par.get(h) ?? 4);
      notes.set(h, `${pts} pt${pts === 1 ? "" : "s"}`);
    }
    return notes;
  },

  describeRules(round, _players, course) {
    return [
      `Stableford across ${holeCount(round, course)} holes: 2 points for a net par, +1 per stroke better, -1 per stroke worse, floored at 0.`,
      `Highest Stableford total wins. Points by finishing place: ${round.pointTable.join(", ")}. Ties split the points evenly.`,
      `Handicap allowance: ${round.handicapAllowance}%.`,
    ];
  },
};
